import React, { useState } from 'react';
import { HistoricalSyncOptions } from '../types';

interface StartSyncFormProps {
  onSubmit: (options: HistoricalSyncOptions, accountName?: string) => void;
  loading: boolean;
}

export const StartSyncForm: React.FC<StartSyncFormProps> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    batchSize: 100,
    concurrentJobs: 100,
    eventNumbers: '', // Keep as string for input handling
    accountName: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Convert string to number array and prepare final options
    const options: HistoricalSyncOptions = {
      batchSize: formData.batchSize,
      concurrentJobs: formData.concurrentJobs,
    };

    // Parse event numbers from comma-separated string
    if (formData.eventNumbers.trim()) {
      const eventNumbers = formData.eventNumbers
        .split(',')
        .map(num => parseInt(num.trim()))
        .filter(num => !isNaN(num));

      if (eventNumbers.length > 0) {
        options.eventNumbers = eventNumbers;
      }
    }

    onSubmit(options, formData.accountName);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="start-sync-form">
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="batchSize">Batch Size</label>
          <input
            type="number"
            id="batchSize"
            name="batchSize"
            value={formData.batchSize}
            onChange={handleInputChange}
            min="1"
            max="1000"
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label htmlFor="concurrentJobs">Concurrent Jobs</label>
          <input
            type="number"
            id="concurrentJobs"
            name="concurrentJobs"
            value={formData.concurrentJobs}
            onChange={handleInputChange}
            min="1"
            max="200"
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label htmlFor="eventNumbers">Event Numbers (Optional)</label>
          <input
            type="text"
            id="eventNumbers"
            name="eventNumbers"
            value={formData.eventNumbers || ''}
            onChange={handleInputChange}
            placeholder="e.g., 12345, 67890, 11111"
            className="form-control"
          />
          <small className="form-help">
            Enter comma-separated event numbers to sync specific events only. Leave empty for all events.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="accountName">Account Name (Optional)</label>
          <input
            type="text"
            id="accountName"
            name="accountName"
            value={formData.accountName || ''}
            onChange={handleInputChange}
            placeholder="e.g. aacc, ewomen, ignite"
            className="form-control"
          />
          <small className="form-help">
            Enter a single account name to sync a specific brushfire account only. Leave empty for all accounts.
          </small>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="submit"
          disabled={loading}
          className="start-sync-button"
        >
          {loading ? '⏳ Starting...' : '🚀 Start Sync'}
        </button>
      </div>
    </form>
  );
};
